import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, Teleport as _Teleport, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "my-2 block text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl" }
const _hoisted_2 = ["datetime", "title"]
const _hoisted_3 = ["datetime"]
const _hoisted_4 = {
  key: 0,
  class: "mt-8 ml-6 sticky top-8 bg-white"
}
const _hoisted_5 = /*#__PURE__*/_createElementVNode("h2", { class: "font-display font-medium text-slate-900" }, "On this page", -1)
const _hoisted_6 = { class: "mt-2 space-y-2 border-l-2 border-slate-100 lg:mt-4 lg:space-y-4 lg:border-slate-200" }

import {
  onMounted, PropType, Ref, ref,
} from 'vue';
import MetaTags from '@/components/MetaTags.vue';
import AuthorBox from '@/components/layout/AuthorBox.vue';
import CopyArticleLink from '@/components/micro/CopyArticleLink.vue';
import { formatDate } from '@/common/dateTime';
import { Author, authorToLdJson } from '@/common/authors';
import Breadcrumbs from '@/components/layout/BreadcrumbsComponent.vue';
import { Breadcrumb } from '@/model/Breadcrumb';
import throttle from '@/common/throttle';
import ContentPage from './ContentPage.vue';
import DnsForDevelopersAd from '../ads/DnsForDevelopersAd.vue';

type TocElement = {
  text: string;
  href: string;
  indent: boolean;
};

// declare a ref to hold the element reference
// the name must match template ref value

export default /*#__PURE__*/_defineComponent({
  __name: 'BlogPost',
  props: {
  author: {
    type: Object as PropType<Author>,
    required: true,
  },
  editor: {
    type: Object as PropType<Author>,
    default: null,
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  url: {
    type: String,
    required: true,
  },
  breadcrumbs: {
    type: Array as PropType<Array<Breadcrumb>>,
    required: true,
  },
  img1024x512: {
    type: String,
    default: null,
  },
  img1200x630: {
    type: String,
    default: null,
  },
  written: {
    type: Date,
    required: true,
  },
  updated: {
    type: Date,
    default: null,
  },
},
  setup(__props) {



const content: Ref<Element | null> = ref(null);
const tocElements: Ref<Array<TocElement>> = ref([]);
const active: Ref<string | undefined> = ref(undefined);

onMounted(() => {
  const headings = (Array.from(content.value?.querySelectorAll('h2,h3') ?? []) as Array<HTMLElement>)
    .filter((h) => !h.classList.contains('ignore-toc'));

  tocElements.value = headings
    .map((h) => {
      const e = h;
      const text = e.innerText.trim();
      e.id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      return {
        text,
        href: `#${e.id}`,
        indent: e.tagName === 'H3',
      };
    });

  const throttledOnScroll = throttle(() => {
    active.value = `#${headings.slice()
      .reverse()
      .find((h) => h.getBoundingClientRect().top < (window.innerHeight / 3))
      ?.id}`;
  }, 100);

  window.addEventListener('scroll', throttledOnScroll);
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      _createVNode(MetaTags, {
        title: __props.title,
        description: __props.description,
        url: __props.url,
        img1024x512: __props.img1024x512,
        img1200x630: __props.img1200x630,
        written: __props.written,
        modified: __props.updated
      }, null, 8, ["title", "description", "url", "img1024x512", "img1200x630", "written", "modified"]),
      (_openBlock(), _createBlock(_resolveDynamicComponent('script'), { type: "application/ld+json" }, {
        default: _withCtx(() => [
          _createTextVNode(" { \"@context\": \"https://schema.org\", \"@type\": \"Article\", \"author\": " + _toDisplayString(_unref(authorToLdJson)(__props.author)) + ", " + _toDisplayString(__props.editor ? `"editor": ${_unref(authorToLdJson)(__props.editor)},` : ``) + " \"copyrightHolder\": { \"@type\": \"Organization\", \"url\": \"https://www.nslookup.io/\", \"email\": \"hello@nslookup.io\" }, \"copyrightYear\": " + _toDisplayString(__props.written.getFullYear()) + ", \"url\": \"https://www.nslookup.io" + _toDisplayString(__props.url) + "\", \"thumbnailUrl\": \"" + _toDisplayString(__props.img1024x512) + "\", \"abstract\": \"" + _toDisplayString(__props.description.replaceAll("\"", "\\\"")) + "\" } ", 1)
        ]),
        _: 1
      }))
    ])),
    _createVNode(ContentPage, null, {
      content: _withCtx(() => [
        _createVNode(Breadcrumbs, {
          class: "w-full max-w-[46rem]",
          routes: __props.breadcrumbs.concat({ url: __props.url, title: __props.title })
        }, null, 8, ["routes"]),
        _createVNode(_component_router_link, {
          to: __props.breadcrumbs[__props.breadcrumbs.length - 1].url,
          class: "block mt-16 text-base text-indigo-600 hover:text-indigo-800 font-semibold tracking-wide uppercase no-underline"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(__props.breadcrumbs[__props.breadcrumbs.length - 1].title), 1)
          ]),
          _: 1
        }, 8, ["to"]),
        _createElementVNode("h1", _hoisted_1, _toDisplayString(__props.title), 1),
        _createVNode(CopyArticleLink),
        (__props.updated)
          ? (_openBlock(), _createElementBlock("time", {
              key: 0,
              datetime: __props.updated.toISOString(),
              class: "block text-sm",
              title: `Written ${_unref(formatDate)(__props.written)}`
            }, "Updated " + _toDisplayString(_unref(formatDate)(__props.updated)), 9, _hoisted_2))
          : (_openBlock(), _createElementBlock("time", {
              key: 1,
              datetime: __props.written.toISOString(),
              class: "block text-sm"
            }, _toDisplayString(_unref(formatDate)(__props.written)), 9, _hoisted_3)),
        _createElementVNode("div", {
          ref_key: "content",
          ref: content
        }, [
          _renderSlot(_ctx.$slots, "default")
        ], 512)
      ]),
      "aside-right": _withCtx(() => [
        _createVNode(AuthorBox, {
          author: __props.author,
          class: "mt-72"
        }, null, 8, ["author"]),
        (tocElements.value.length > 1)
          ? (_openBlock(), _createElementBlock("nav", _hoisted_4, [
              _hoisted_5,
              _createElementVNode("ul", _hoisted_6, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tocElements.value, (tocElement) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: tocElement.href,
                    class: "relative"
                  }, [
                    _createVNode(_component_router_link, {
                      class: _normalizeClass([
              'block w-full before:pointer-events-none before:absolute before:-left-1 before:top-1/2 before:h-1.5 before:w-1.5 before:-translate-y-1/2 before:rounded-full',
              tocElement.indent ? 'pl-7' : 'pl-3.5',
              active.value === tocElement.href ? 'font-semibold text-indigo-500 before:bg-indigo-500' : 'text-slate-500 before:hidden before:bg-slate-300 hover:text-slate-600 hover:before:block',
            ]),
                      to: tocElement.href
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(tocElement.text), 1)
                      ]),
                      _: 2
                    }, 1032, ["class", "to"])
                  ]))
                }), 128))
              ]),
              _createVNode(DnsForDevelopersAd, { class: "mt-10" })
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 3
    })
  ], 64))
}
}

})